import ContactFilters from "@/helpers/ContactFilters";

/**
 * Frozen workflow action-type catalog + display helpers.
 *
 * Keys and shapes are a Core/UI contract (canvas, editors, activity feeds).
 * Do not reorder `actionTypes` keys — unit tests lock the catalog order.
 * Catalog `description` strings and activity HTML may include intentional
 * `<b>` / `<strong>` for `v-html` consumers.
 */
export default {
  actionTypes: {
    // --- Control flow ---
    'new': {
      title: 'New',
      description: 'Edit to set type & details.',
      slots: 0,
    },
    'delay': {
      title: 'Delay',
      description: 'Wait specified <b>amount</b> of time.',
      slots: 1,
      delay: true,
    },
    'if': {
      title: 'Condition',
      description: 'Check specific condition.',
      slots: 2,
      contactFilter: true,
      timeout: true,
    },
    'random': {
      title: 'Random',
      description: 'Select the next step randomly.',
      spread: true,
    },
    // --- CRM: lists / tags / notes / tasks ---
    'list_add': {
      title: 'Add to List',
      description: 'Add to the contact list.',
      slots: 1,
      contactList: true,
      limit: true,
    },
    'list_remove': {
      title: 'Remove from List',
      description: 'Remove from the contact list.',
      slots: 1,
      contactList: true,
      limit: true,
    },
    'tag_add': {
      title: 'Add Tag',
      description: 'Add tag to the contact.',
      slots: 1,
      tag: true,
      limit: true,
    },
    'tag_remove': {
      title: 'Remove Tag',
      description: 'Remove tag from the contact.',
      slots: 1,
      tag: true,
      limit: true,
    },
    'note_add': {
      title: 'Add Note',
      description: 'Add note to the contact.',
      slots: 1,
      note: true,
      limit: true,
    },
    'note_remove': {
      title: 'Remove Note',
      description: 'Remove note from the contact.',
      slots: 1,
      note: true,
      limit: true,
    },
    'task_add': {
      title: 'Add Task',
      description: 'Add to the task.',
      slots: 1,
      task: true,
      limit: true,
    },
    'task_remove': {
      title: 'Remove Task',
      description: 'Remove from the task.',
      slots: 1,
      task: true,
      limit: true,
    },
    // --- Nested workflow control ---
    'workflow_enroll': {
      title: 'Enroll into Workflow',
      description: 'Enroll into the workflow.',
      slots: 1,
      workflow: true,
      limit: true,
      spread: true,
    },
    'workflow_pause': {
      title: 'Pause in Workflow',
      description: 'Pause contact in the workflow.',
      slots: 1,
      workflow: true,
      limit: true,
    },
    'workflow_resume': {
      title: 'Resume in Workflow',
      description: 'Resume contact in the workflow.',
      slots: 1,
      workflow: true,
      limit: true,
    },
    // --- LinkedIn actions ---
    'linkedin_view_profile': {
      title: 'View Profile',
      description: 'Visit LinkedIn profile page.',
      slots: 1,
      linkedin: true,
      limit: true,
      spread: true,
    },
    'linkedin_follow_profile': {
      title: 'Follow Profile',
      description: 'Follow LinkedIn profile.',
      slots: 1,
      linkedin: true,
      limit: true,
      spread: true,
    },
    'linkedin_unfollow_profile': {
      title: 'Unfollow Profile',
      description: 'Unfollow LinkedIn profile.',
      slots: 1,
      linkedin: true,
      limit: true,
      spread: true,
    },
    'linkedin_endorse_profile': {
      title: 'Endorse Profile',
      description: 'Endorse LinkedIn profile.',
      slots: 1,
      linkedin: true,
      limit: true,
      spread: true,
    },
    'linkedin_like_post': {
      title: 'Like Post',
      description: 'Like a recent post by LinkedIn profile.',
      slots: 1,
      linkedin: true,
      limit: true,
      spread: true,
    },
    'linkedin_message_profile': {
      title: 'Message Profile',
      description: 'Send a message to LinkedIn profile.',
      slots: 1,
      linkedin: true,
      details: {
        'message': '',
      },
      limit: true,
      spread: true,
    },
    'linkedin_connect_profile': {
      title: 'Connect Profile',
      description: 'Invite LinkedIn profile to connect.',
      slots: 1,
      linkedin: true,
      details: {
        'message': '',
      },
      limit: true,
      spread: true,
    },
    'linkedin_withdraw_invitation': {
      title: 'Withdraw Invitation',
      description: 'Withdraw invitation to connect on LinkedIn.',
      slots: 1,
      linkedin: true,
      limit: true,
      spread: true,
    },
  },

  /** Display title for a catalog type (falls back to the raw type string). */
  getTypeTitle (type) {
    return (this.actionTypes[type]) ? this.actionTypes[type]['title'] : type;
  },

  /**
   * HTML description for canvas/activity nodes.
   * When `data` has resolved entities, prefers filled copy; else catalog default.
   */
  getTypeDescription (type, data, workflow) {
    if (!data) data = {};
    if (!workflow) workflow = {};
    if (type == 'delay') {
      if (data.delay) {
        return 'Wait <b>' + data.delay + '</b> hours.';
      }
    } else if (type == 'if') {
      if (data.contact_filter) {
        return 'If ' + ContactFilters().getSimpleDescription(workflow.account_id, workflow.id, data.contact_filter, true) + '.';
      }
    } else if (type == 'list_add') {
      if (data.contact_list) {
        return 'Add to the <b>' + data.contact_list.name + '</b> contact list.';
      }
    } else if (type == 'list_remove') {
      if (data.contact_list) {
        return 'Remove from the <b>' + data.contact_list.name + '</b> contact list.';
      }
    } else if (type == 'tag_add') {
      if (data.tag) {
        return 'Add <b>' + data.tag.label + '</b> tag to the contact.';
      }
    } else if (type == 'tag_remove') {
      if (data.tag) {
        return 'Remove <b>' + data.tag.label + '</b> tag from the contact.';
      }
    } else if (type == 'note_add') {
      if (data.note) {
        return 'Add <b>' + data.note.label + '</b> note to the contact.';
      }
    } else if (type == 'note_remove') {
      if (data.note) {
        return 'Remove <b>' + data.note.label + '</b> note from the contact.';
      }
    } else if (type == 'task_add') {
      if (data.task) {
        return 'Add to the <b>' + data.task.label + '</b> task.';
      }
    } else if (type == 'task_remove') {
      if (data.task) {
        return 'Remove from the <b>' + data.task.label + '</b> task.';
      }
    } else if (type == 'workflow_enroll') {
      if (data.workflow) {
        return 'Enroll into the <b>' + data.workflow.name + '</b> workflow.';
      }
    } else if (type == 'workflow_pause') {
      if (data.workflow) {
        return 'Pause contact in the <b>' + data.workflow.name + '</b> workflow.';
      }
    } else if (type == 'workflow_resume') {
      if (data.workflow) {
        return 'Resume contact in the <b>' + data.workflow.name + '</b> workflow.';
      }
    }
    return (this.actionTypes[type]) ? this.actionTypes[type]['description'] : '';
  },

  /** Max child slots for a type (`undefined` when the type has no fixed slot count). */
  getTypeSlots (type) {
    return (this.actionTypes[type]) ? this.actionTypes[type]['slots'] : undefined;
  },

  /** Shallow copy of default `details` so canvas edits do not mutate the catalog. */
  getTypeDetails (type) {
    return (this.actionTypes[type]) ? { ...this.actionTypes[type]['details'] } : undefined;
  },

  /** Timeline/dot color for a workflow activity event. */
  getEventColor(event) {
    if (event.type == 'start') {
      return 'blue';
    } else if (event.type == 'finish') {
      return 'blue';
    } else if (event.result) {
      return 'green';
    } else if (event.result === false) {
      return 'red';
    }
    return 'grey';
  },

  /** HTML summary line for a workflow activity event (`v-html` safe consumer copy). */
  getEventDescription(event) {
    var description = '';

    if (event.result) {
      description = '<strong>Successfully</strong> ';
    } else if (event.result === false) {
      description = '<strong>Unsuccessfully</strong> ';
    }

    if (event.type == 'start') {
      description += 'started workflow.';
    } else if (event.type == 'finish') {
      description += 'finished workflow.';
    } else if (event.type == 'delay') {
      description += 'waited specified amount of time.';
    } else if (event.type == 'random') {
      description += 'selected random next step.';
    } else if (event.type == 'if') {
      description = event.result ? 'Passed condition check.' : 'Did not pass condition check.';
    } else if (event.type == 'list_add') {
      description += 'added to contact list.';
    } else if (event.type == 'list_remove') {
      description += 'removed from contact list.';
    } else if (event.type == 'tag_add') {
      description += 'added tag.';
    } else if (event.type == 'tag_remove') {
      description += 'removed tag.';
    } else if (event.type == 'note_add') {
      description += 'added note.';
    } else if (event.type == 'note_remove') {
      description += 'removed note.';
    } else if (event.type == 'task_add') {
      description += 'added to task.';
    } else if (event.type == 'task_remove') {
      description += 'removed from task.';
    } else if (event.type == 'workflow_enroll') {
      description += 'enrolled into workflow.';
    } else if (event.type == 'workflow_pause') {
      description += 'paused in workflow.';
    } else if (event.type == 'workflow_resume') {
      description += 'resumed in workflow.';
    } else if (event.type == 'linkedin_view_profile') {
      description += 'visited on LinkedIn.';
    } else if (event.type == 'linkedin_follow_profile') {
      description += 'followed on LinkedIn.';
    } else if (event.type == 'linkedin_unfollow_profile') {
      description += 'unfollowed on LinkedIn.';
    } else if (event.type == 'linkedin_endorse_profile') {
      description += 'endorsed on LinkedIn.';
    } else if (event.type == 'linkedin_like_post') {
      description += 'liked their post on LinkedIn.';
    } else if (event.type == 'linkedin_message_profile') {
      description += 'sent a message via LinkedIn.';
    } else if (event.type == 'linkedin_connect_profile') {
      description += 'invited on LinkedIn to connect.';
    } else if (event.type == 'linkedin_withdraw_invitation') {
      description += 'withdrawn invitation to connect on LinkedIn.';
    } else {
      description += event.type;
    }

    return description;
  },
};
